import ftmcLogo from "../Assets/Images/ftmc_logo.jpg";
import IconButton from "@mui/material/IconButton";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import Paper from "@mui/material/Paper";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Avatar } from "@mui/material";

import { useAuth } from "./Auth/useAuth";
import { getPortalLabel } from "../portal/portalUtils.js";

function PersonaControls() {
  const {
    viewer,
    activePortal,
    hostPortal,
    personaPortal,
    availablePersonas,
    isImpersonating,
    setPersonaPortal,
    clearPersonaPortalOverride,
  } = useAuth();

  if (!viewer?.canSwitchPersona || isImpersonating) {
    return null;
  }

  return (
    <div className="hidden lg:flex items-center gap-2">
      <span className="text-white text-sm uppercase tracking-wide">
        {!personaPortal
          ? `${getPortalLabel(hostPortal)} workspace`
          : `Viewing as ${getPortalLabel(activePortal)}`}
      </span>
      {availablePersonas.filter(portal => portal !== hostPortal).map((portal) => (
        <Button
          key={portal}
          variant={activePortal === portal ? "contained" : "outlined"}
          color="inherit"
          size="small"
          onClick={() => setPersonaPortal(portal)}
          sx={{ minWidth: 0, textTransform: "none" }}
        >
          {getPortalLabel(portal)}
        </Button>
      ))}
      {personaPortal && (
        <Button
          variant="text"
          color="inherit"
          size="small"
          onClick={clearPersonaPortalOverride}
          sx={{ minWidth: 0, textTransform: "none" }}
        >
          Return to {getPortalLabel(hostPortal)}
        </Button>
      )}
    </div>
  );
}

function ImpersonationControls() {
  const { impersonatedEmail, isImpersonating, stopImpersonation } = useAuth();

  if (!isImpersonating) {
    return null;
  }

  return (
    <div className="hidden lg:flex items-center gap-2 rounded-full border border-white/60 bg-[#8f2d1f] px-3 py-1 text-white">
      <span className="max-w-[220px] truncate text-sm">
        Impersonating {impersonatedEmail || "user"}
      </span>
      <button
        aria-label="Unset impersonation"
        className="flex h-6 w-6 items-center justify-center rounded-full border border-white/70 text-white transition hover:bg-white/10"
        onClick={stopImpersonation}
        type="button"
      >
        <CloseIcon sx={{ fontSize: 14 }} />
      </button>
    </div>
  );
}

export default function Header({ hideAvatar = false }) {
  const navigate = useNavigate();
  const { user, logout, activePortal } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Paper
      data-testid="app-header"
      style={{ backgroundColor: "rgb(241,93,34)", borderRadius: "0" }}
      elevation={3}
      className="flex w-full h-[64px] px-4 md:px-6 justify-between shadow-2xl z-50"
    >
      <div className="flex min-w-0 items-center gap-4">
        <IconButton
          aria-label="Open navigation"
          className="md:!hidden"
          onClick={() => window.dispatchEvent(new Event("ftmc:toggle-navigation"))}
          size="small"
          sx={{ color: "white" }}
        >
          <MenuIcon />
        </IconButton>
        <Link to="/dashboard">
          <div className="flex h-full max-w-[180px] items-center md:max-w-[280px]">
            <img
              className="block max-h-[52px] w-full object-contain md:max-h-[56px]"
              src={ftmcLogo}
              alt="fmtc logo"
            />
          </div>
        </Link>
        <span className="hidden md:block text-white text-sm uppercase tracking-[0.16em]">
          {getPortalLabel(activePortal)} Portal
        </span>
      </div>

      <div className={`my-auto flex items-center gap-4 ${hideAvatar && "hidden"}`}>
        <PersonaControls />
        <ImpersonationControls />

        <IconButton aria-label="profile" size="large" onClick={handleClick}>
          {user !== null ? (
            <Avatar alt="profile picture" src={user.picture} />
          ) : (
            <AccountCircleIcon className="text-white" fontSize="inherit" />
          )}
        </IconButton>

        <p className="text-white hidden min-[550px]:block mr-4 text-[18px]">
          {user !== null ? `Hi, ${user.name}` : "Hi, Guest"}
        </p>
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "basic-button",
          }}
        >
          <MenuItem
            onClick={() => {
              handleClose();
              navigate("/profile");
            }}
          >
            Profile
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleClose();
              logout();
            }}
          >
            Logout
          </MenuItem>
        </Menu>
      </div>
    </Paper>
  );
}
